// @flow
import React, { useEffect, useState } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import moment from 'moment';
import { gql, useQuery } from '@apollo/client';
import { REGIONS } from 'settings';
import Dropdown from './Dropdown';
import { S, DARK } from '~/app/ui/tokens';

const getTagsFromPages = (pages) => {
  const tags = [];

  pages.forEach((page) => {
    page.template.props.tags.forEach((tag) => {
      if (!tags.includes(tag.tag)) {
        tags.push(tag.tag);
      }
    });
  });

  return tags.sort();
};

type Article = {
  hero_image: string,
  title: string,
  published_at: Date,
  description: string,
  tags: {
    tag: string
  }[],
  url: string
};

type FutureSightPost = {
  path: string,
  template: {
    props: {
      url: string,
      title: string,
      published_at: Date,
      description: string,
      heroImageUrl: string,
      region: 'US' | 'CN',
      featured: boolean,
      tags: {
        tag: string
      }[],
    }
  }
};

type Props = {
  classes: Object,
  value: Array<Article>,
  onChange: Function
};

const FutureSightConnect = (props: Props) => {
  const {
    value, onChange, classes: c
  } = props;

  const [tags, setTags] = useState<string[]>([]);

  // Filters
  const [appliedTags, setAppliedTags] = useState<string[]>([]);
  const [appliedRegion, setAppliedRegion] = useState('');
  const [appliedFeatured, setAppliedFeatured] = useState(false);

  // getFutureSightPosts
  const { data } = useQuery(QUERY);
  const futureSightPosts: FutureSightPost[] = data?.futureSightPosts;

  // Create list of tags after page info is loaded
  useEffect(() => {
    if (futureSightPosts) {
      setTags(getTagsFromPages(futureSightPosts));
    }
  }, [futureSightPosts]);

  // Update props to include/remove selected page
  const handleClick = (page) => {
    if (!value.find(pageProps => page.path === pageProps.url)) {
      onChange([
        ...value,
        page.template.props
      ]);
    } else {
      onChange(value.filter(pageProps => page.path !== pageProps.url));
    }
  };

  const toggleTag = (tag) => {
    if (appliedTags.includes(tag)) {
      setAppliedTags(appliedTags.filter(t => t !== tag));
    } else {
      setAppliedTags([...appliedTags, tag]);
    }
  };

  const toggleFeatured = () => { setAppliedFeatured(!appliedFeatured); };

  const selectedPages = value.map(pageProps => pageProps.url);

  // Apply filters to full page list
  const pages = futureSightPosts ?
    futureSightPosts.filter(page => (
      (appliedTags.length === 0 || appliedTags.every(tag => page.template.props.tags.find(t => tag === t.tag))) &&
      (!appliedRegion || page.template.props.region.toUpperCase() === appliedRegion) &&
      (!appliedFeatured || page.template.props.featured)
    )) : [];

  const controls = (
    <div className={c.controls}>
      <div>
        <div className={c.controlLabel}>Region</div>
        <Dropdown
          value={appliedRegion}
          options={[''].concat(REGIONS)}
          onChange={setAppliedRegion}
        />
      </div>
      <div>
        <div className={c.controlLabel}>Tags</div>
        <div className={c.filterTags}>
          {tags.map(tag => (
            <div
              className={c.radioBoxContainer}
              onClick={() => toggleTag(tag)}
              key={tag}
            >
              <div className={classNames(c.checkbox, appliedTags.includes(tag) && c.checked)} />
              <span className={c.label}>
                {tag}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className={c.controlLabel}>Featured</div>
        <div className={c.filterTags}>
          <div
            className={c.radioBoxContainer}
            onClick={toggleFeatured}
          >
            <div className={classNames(c.checkbox, appliedFeatured && c.checked)} />
            <span className={c.label}>
                Featured
            </span>
          </div>
        </div>
      </div>
    </div>
  );
  const list = (
    pages.map((page, i) => (
      <div
        className={classNames(c.article, selectedPages.includes(page.path) && c.selected)}
        key={i}
        onClick={() => handleClick(page)}
      >
        <div
          className={c.heroImg}
          style={{
            backgroundImage: `url(${page.template.props.heroImageUrl})`
          }}
        />
        <div className={c.date}>{moment(page.template.props.published_at).format('MMMM DD, YYYY')}</div>
        <div className={c.title}>
          <a href={page.template.props.url}>{page.template.props.title}</a>
        </div>
        <div className={c.desc}>{page.template.props.description}</div>
        <div className={c.tags}>
          <i className="material-icons">mail</i>
          {page.template.props.tags.map(tag => (
            <a href={tag.tag} key={tag.tag}>{tag.tag}</a>
          ))}
        </div>
      </div>
    ))
  );

  return (
    <div className={c.container}>
      {controls}
      {list}
    </div>
  );
};

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  container: {
    width: '100%'
  },
  article: {
    backgroundColor: 'white',
    [DARK]: { backgroundColor: S.chrome },
    margin: '18px 10px',
    paddingBottom: '18px',
    display: 'inline-block',
    width: '30%',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all .2s ease',
    '&:hover': {
      '&:after': {
        content: '"add_circle"',
        position: 'absolute',
        fontFamily: 'Material Icons',
        opacity: '.3',
        left: '0',
        right: '0',
        top: '0',
        bottom: '0',
        width: '100%',
        height: '100%',
        display: 'flex',
        color: 'white',
        fontSize: '18px',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#141618'
      }
    }
  },
  selected: {
    '&:after': {
      content: '"check_circle"',
      position: 'absolute',
      fontFamily: 'Material Icons',
      left: '0',
      right: '0',
      top: '0',
      bottom: '0',
      width: '100%',
      height: '100%',
      display: 'flex',
      fontSize: '38px',
      color: 'hsl(160, 50%, 50%)',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'hsla(160, 50%, 50%, .1)'
    },
    '&:hover': {
      '&:after': {
        content: '"remove_circle"',
        position: 'absolute',
        fontFamily: 'Material Icons',
        opacity: '.3',
        left: '0',
        right: '0',
        top: '0',
        bottom: '0',
        width: '100%',
        height: '100%',
        display: 'flex',
        color: 'white',
        fontSize: '18px',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#141618'
      }
    }
  },
  heroImg: {
    width: '100%',
    height: '20%',
    maxHeight: '194px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center center'
  },
  date: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1.75em',
    margin: '9px 18px',
  },
  title: {
    fontSize: '15px',
    fontWeight: '300',
    letterSpacing: '.025em',
    lineHeight: '1.5em',
    transition: 'all .2s ease',
    marginLeft: '18px',
    marginRight: '18px',
    paddingBottom: '9px',
    marginBottom: '9px',
    borderBottom: '1px solid #eaeaea',
    cursor: 'pointer',
    '& > a': {
      color: 'black',
      textDecoration: 'none'
    },
    [DARK]: { borderBottomColor: S.line, '& > a': { color: S.fg } }
  },
  desc: {
    margin: '9px 18px',
    marginBottom: '18px',
    fontSize: '15px',
    lineHeight: '1.75em'
  },
  readMore: {
    display: 'block',
    margin: '9px 18px',
    fontSize: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    color: 'black',
    textTransform: 'uppercase',
    [DARK]: { color: S.fg }
  },
  tags: {
    margin: '9px 18px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > a': {
      textDecoration: 'none',
      color: 'black',
      textTransform: 'capitalize',
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '1.75em',
      marginRight: '5px',
      transition: 'all .2s ease',
      '&:after': {
        content: '","',
        display: 'inline-block'
      },
      '&:last-child:after': {
        content: '""'
      }
    },
    [DARK]: { '& > a': { color: S.fg } }
  },
  controls: {
    display: 'flex',
    '& > div': {
      marginRight: '20px'
    }
  },
  controlLabel: {
    fontSize: '12px',
    fontWeight: '300',
    marginTop: '15px'
  },
  filterTags: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  radioBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
    cursor: 'pointer',
    marginRight: '20px'
  },
  radioBox: {
    width: '16px',
    height: '16px',
    marginRight: '5px',
    padding: '3px',
    transition: 'background ease .2s',
    borderRadius: '8px',
    boxShadow: '0 0 3px #aaa',
    [DARK]: { boxShadow: `0 0 3px ${S.fieldLine}` }
  },
  checkbox: {
    width: '16px',
    height: '16px',
    outline: '1px solid #ccc',
    marginRight: '5px',
    padding: '3px',
    transition: 'background ease .2s',
    borderRadius: '2px',
    boxShadow: '0 0 3px #bbb',
    [DARK]: { outlineColor: S.fieldLine, boxShadow: `0 0 3px ${S.fieldLine}` }
  },
  checked: {
    background: 'hsl(160, 50%, 50%)',
    border: '3px solid white',
    [DARK]: { borderColor: S.field }
  },
  label: {
    fontWeight: '600',
    fontSize: '12px'
  }
};

/* =============================================================================
=    GraphQL: Get pages
============================================================================= */
const QUERY = gql`
  query getData {
    futureSightPosts {
      path
      template {
        templateId
        props
      }
    }
  }
`;

export default injectSheet(styles)(FutureSightConnect);
